import React, { useState, useCallback } from 'react'
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  OutlinedInput,
} from '@mui/material'
import { Close } from '@mui/icons-material'
import { USAGE_TYPES, DATA_TYPES } from '../../constants'

export interface ElementFieldsProps {
  element: any
  elementIndex: number
  readonly?: boolean
  onFieldChange: (elementIndex: number, field: string, value: any) => void
  showDescription?: boolean
  showNotes?: boolean
  showCodes?: boolean
  showDefault?: boolean
}

const ElementFields = React.memo<ElementFieldsProps>(
  ({
    element,
    elementIndex,
    readonly = false,
    onFieldChange,
    showDescription = true,
    showNotes = true,
    showCodes = true,
    showDefault = true,
  }) => {
    const [chipInputValue, setChipInputValue] = useState('')

    // Handler for chip input change
    const handleChipInputChange = useCallback((value: string) => {
      setChipInputValue(value)
    }, [])

    // Handler for adding chips
    const handleChipInputKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && chipInputValue.trim()) {
          event.preventDefault()
          const newCode = chipInputValue.trim()
          const currentCodes = element.codes || []

          // Don't add duplicate codes
          if (!currentCodes.includes(newCode)) {
            const updatedCodes = [...currentCodes, newCode]
            onFieldChange(elementIndex, 'codes', updatedCodes)
          }

          setChipInputValue('')
        }
      },
      [chipInputValue, element.codes, onFieldChange, elementIndex]
    )

    // Handler for removing chips
    const handleChipDelete = useCallback(
      (codeToDelete: string) => {
        const currentCodes = element.codes || []
        const updatedCodes = currentCodes.filter(code => code !== codeToDelete)
        onFieldChange(elementIndex, 'codes', updatedCodes)
      },
      [element.codes, onFieldChange, elementIndex]
    )

    return (
      <>
        {/* Main row with core fields */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs="auto">
            <TextField
              label="Position"
              type="number"
              value={element.position || ''}
              onChange={e => onFieldChange(elementIndex, 'position', e.target.value)}
              disabled={readonly}
              size="small"
              sx={{ maxWidth: 90 }}
            />
          </Grid>
          <Grid item xs>
            <TextField
              fullWidth
              label="Name"
              value={element.name || ''}
              onChange={e => onFieldChange(elementIndex, 'name', e.target.value)}
              disabled={readonly}
              size="small"
              sx={{ minWidth: 90 }}
            />
          </Grid>
          <Grid item xs="auto">
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <InputLabel id="usage-label">Usage</InputLabel>
              <Select
                labelId="usage-label"
                value={element.usage || 'M'}
                onChange={e => onFieldChange(elementIndex, 'usage', e.target.value)}
                disabled={readonly}
                label="Usage"
              >
                {USAGE_TYPES.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs="auto">
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="data-type-label">Data Type</InputLabel>
              <Select
                labelId="data-type-label"
                value={element.data_type || 'string'}
                onChange={e => onFieldChange(elementIndex, 'data_type', e.target.value)}
                disabled={readonly}
                label="Data Type"
              >
                {DATA_TYPES.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs="auto">
            <TextField
              label="Min Length"
              type="number"
              value={element.min_length || ''}
              onChange={e => onFieldChange(elementIndex, 'min_length', e.target.value)}
              disabled={readonly}
              size="small"
              sx={{ maxWidth: 90 }}
            />
          </Grid>
          <Grid item xs="auto">
            <TextField
              label="Max Length"
              type="number"
              value={element.max_length || ''}
              onChange={e => onFieldChange(elementIndex, 'max_length', e.target.value)}
              disabled={readonly}
              size="small"
              sx={{ maxWidth: 90 }}
            />
          </Grid>
          {showDefault && (
            <Grid item xs="auto">
              <TextField
                label="Default"
                value={element.default_data || element.default_value || ''}
                onChange={e => onFieldChange(elementIndex, 'default_data', e.target.value)}
                disabled={readonly}
                size="small"
                sx={{ minWidth: 120 }}
              />
            </Grid>
          )}
        </Grid>

        {/* Description row */}
        {showDescription && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={element.description || ''}
                onChange={e => onFieldChange(elementIndex, 'description', e.target.value)}
                disabled={readonly}
                size="small"
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        )}

        {/* Notes row */}
        {showNotes && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={element.notes || ''}
                onChange={e => onFieldChange(elementIndex, 'notes', e.target.value)}
                disabled={readonly}
                size="small"
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        )}

        {/* Codes row */}
        {showCodes && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl size="small" disabled={readonly} sx={{ width: '100%' }}>
                <InputLabel>Codes</InputLabel>
                <OutlinedInput
                  label="Codes"
                  value={chipInputValue}
                  onChange={e => handleChipInputChange(e.target.value)}
                  onKeyDown={handleChipInputKeyDown}
                  disabled={readonly}
                  placeholder="Type a code and press Enter to add..."
                  startAdornment={
                    element.codes &&
                    element.codes.length > 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          flexWrap: 'nowrap',
                          gap: 0.5,
                          mr: 1,
                          alignItems: 'center',
                          minHeight: '32px',
                          overflow: 'visible',
                          maxWidth: 'calc(100% - 20px)',
                        }}
                      >
                        {element.codes.map((code: string, codeIndex: number) => (
                          <Chip
                            key={codeIndex}
                            label={code}
                            size="small"
                            onDelete={readonly ? undefined : () => handleChipDelete(code)}
                            deleteIcon={readonly ? undefined : <Close />}
                            sx={{ height: 24, fontSize: '0.75rem' }}
                          />
                        ))}
                      </Box>
                    )
                  }
                />
              </FormControl>
            </Grid>
          </Grid>
        )}
      </>
    )
  }
)

ElementFields.displayName = 'ElementFields'

export default ElementFields
